import React from 'react';

type DashboardDateInputProps = {
  value: string;
  min: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
  ariaLabel: string;
  displayValue?: string;
  placeholder?: string;
  title?: string;
  disabled?: boolean;
  wrapperClassName?: string;
};

type DateInputElement = HTMLInputElement & {
  showPicker?: () => void;
};

const DashboardDateInput: React.FC<DashboardDateInputProps> = ({
  value,
  min,
  onChange,
  ariaLabel,
  displayValue,
  placeholder = 'Select date',
  title,
  disabled = false,
  wrapperClassName = '',
}) => {
  const resolvedDisplayValue = displayValue || value || placeholder;
  const inputRef = React.useRef<DateInputElement | null>(null);

  const openPicker = () => {
    const input = inputRef.current;
    if (!input || disabled) return;

    input.focus();
    if (typeof input.showPicker === 'function') {
      try {
        input.showPicker();
        return;
      } catch {
        // Fall back for browsers that expose showPicker but reject it.
      }
    }
    input.click();
  };

  return (
    <div className={wrapperClassName}>
      <div className="relative">
        <input
          ref={inputRef}
          type="date"
          value={value}
          min={min}
          onChange={onChange}
          disabled={disabled}
          title={title}
          aria-hidden="true"
          tabIndex={-1}
          className="dashboard-date-input peer absolute inset-0 z-0 min-h-[44px] w-full rounded-[10px] border px-3 py-2 pl-9 pr-9 text-center text-[11px] [color-scheme:light] opacity-0"
        />
        <button
          type="button"
          onClick={openPicker}
          disabled={disabled}
          title={title}
          aria-label={ariaLabel}
          className={`relative z-[1] flex min-h-[44px] w-full items-center justify-center rounded-[10px] border px-3 py-2 pl-9 pr-9 text-center text-[11px] transition focus-visible:ring-2 focus-visible:ring-[var(--qcl-border)] focus-visible:ring-offset-2 ${
            disabled
              ? 'cursor-not-allowed border-[var(--qcl-border-light)] bg-[var(--qcl-border-light)] text-[var(--qcl-text-muted)]'
              : 'cursor-pointer border-[var(--qcl-border)] bg-[var(--qcl-bg)] text-[var(--qcl-text-dim)] hover:bg-white'
          }`}
        >
          <span
            aria-hidden="true"
            className="absolute left-3 top-1/2 -translate-y-1/2 text-[13px]"
          >
            📅
          </span>
          <span className="qcl-mono w-full truncate text-center">{resolvedDisplayValue}</span>
        </button>
      </div>
    </div>
  );
};

export default DashboardDateInput;
